import _sortBy from 'lodash-es/sortBy';
import { Slice } from '@teetsh/app/src/types/print';
import { CJEDTEvent } from '@teetsh/app/src/types/event';
import differenceInMinutes from '@teetsh/app/src/utils/time/differenceInMinutes';
import { format } from '@teetsh/app/src/utils/time/format';
import setMinutes from '@teetsh/app/src/utils/time/setMinutes';
import setHours from '@teetsh/app/src/utils/time/setHours';
import isAfter from '@teetsh/app/src/utils/time/isAfter';
import isBefore from '@teetsh/app/src/utils/time/isBefore';
import { getMonday } from '@teetsh/app/src/utils/time/getMonday';

export const getDateHHMM = date => format(date, 'fr-FR', "HH'h'mm");

export function getSlices(events: CJEDTEvent[]): Slice[] {
  const slices = new Map();
  events.forEach(event => {
    slices.set(getDateHHMM(event.start), '');
    slices.set(getDateHHMM(event.end), '');
  });

  const slicesSorted = _sortBy(Array.from(slices.keys()));
  if (slicesSorted.length === 1) {
    // special case when all events (1 or more) have the same start and end time (no idea why but we should safely handle it)
    return [
      {
        start: slicesSorted[0],
        end: slicesSorted[0],
      },
    ];
  }

  const result: Slice[] = [];
  slicesSorted.forEach((slice, index) => {
    if (index < slicesSorted.length - 1) {
      result.push({
        start: slice,
        end: slicesSorted[index + 1],
      });
    }
  });

  return result;
}

export function getSliceAtDate(slice: Slice, date: Date): [Date, Date] {
  const [startHour, startMinutes] = slice.start.split('h');
  const startSlice = setHours(
    setMinutes(date, parseInt(startMinutes, 10)),
    parseInt(startHour, 10),
  );

  const [endHour, endMinutes] = slice.end.split('h');
  const endSlice = setHours(
    setMinutes(date, parseInt(endMinutes, 10)),
    parseInt(endHour, 10),
  );
  return [startSlice, endSlice];
}

export const doesEventIntersectSlice = (event, { start, end }) => {
  const [startSlice, endSlice] = getSliceAtDate({ start, end }, event.start);

  return (
    (isAfter(event.start, startSlice) && isBefore(event.start, endSlice)) ||
    (isAfter(event.end, startSlice) && isBefore(event.end, endSlice)) ||
    (isBefore(event.start, startSlice) && isAfter(event.end, endSlice))
  );
};

export const getSlicesOfTheEvent = (event: CJEDTEvent, slices: Slice[]) =>
  slices.filter(
    slice =>
      getDateHHMM(event.start) === slice.start ||
      getDateHHMM(event.end) === slice.end ||
      doesEventIntersectSlice(event, slice),
  );

export const isEventHappeningDuringSlice = (event, { start, end }) => {
  const [startSlice, endSlice] = getSliceAtDate({ start, end }, event.start);
  return (
    (isAfter(startSlice, event.start) && isBefore(endSlice, event.end)) ||
    (isAfter(event.start, startSlice) && isBefore(event.end, endSlice))
  );
};

export function getEventsHappeningDuringTheSlice(
  events: CJEDTEvent[],
  { start, end },
): {
  eventsHappeningDuring: CJEDTEvent[];
  eventsStarting: CJEDTEvent[];
  eventsEnding: CJEDTEvent[];
  eventsStartingAndEnding: CJEDTEvent[];
  allEvents: CJEDTEvent[];
} {
  const eventsHappeningDuring: CJEDTEvent[] = events.filter(e =>
    isEventHappeningDuringSlice(e, { start, end }),
  );

  const eventsStarting = events.filter(
    e => getDateHHMM(e.start) === start && !(getDateHHMM(e.end) === end),
  );

  const eventsEnding = events.filter(
    e => getDateHHMM(e.end) === end && !(getDateHHMM(e.start) === start),
  );
  const eventsStartingAndEnding = events.filter(
    e => getDateHHMM(e.start) === start && getDateHHMM(e.end) === end,
  );

  return {
    eventsHappeningDuring,
    eventsStarting,
    eventsEnding,
    eventsStartingAndEnding,
    allEvents: [
      ...eventsHappeningDuring,
      ...eventsStarting,
      ...eventsEnding,
      ...eventsStartingAndEnding,
    ],
  };
}

export function timeBetweenSlices(startSlice, endSlice) {
  const [start, end] = getSliceAtDate(
    { start: startSlice, end: endSlice },
    getMonday(12, 0),
  );

  return differenceInMinutes(end, start);
}

export function getMaxSliceForEvents(events: CJEDTEvent[]): Slice {
  const slices = getSlices(events);

  return {
    start: slices[0].start,
    end: slices[slices.length - 1].end,
  };
}
